import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { parseLocaleNumber } from "../../../../utils/locale-number-utils";

/**
 * Shared UI primitives and design tokens for the four GDS cell extract dialogs.
 * The app is always light, so the tokens are the light palette: primary #3949AB
 * plus the four semantic colors (used, leftover, lent, substitution).
 */
export const T = {
  surface: "#FFFFFF",
  surfaceSunken: "#F5F7FA",
  line: "#DCE3EA",
  lineStrong: "#C3CDD7",
  ink: "#2E3A44",
  inkSoft: "#5A6B78",
  inkFaint: "#8A9AA6",
  accent: "#3949AB",
  accentSoft: "#E7E9F7",
  accentInk: "#2C3789",
  used: "#3949AB",
  usedSoft: "#E7E9F7",
  left: "#2E7D5B",
  leftSoft: "#DFF0E8",
  lent: "#B26A00",
  lentSoft: "#FBEEDA",
  sub: "#6A3EA1",
  subSoft: "#EEE6F8",
  neg: "#B3261E",
  mono: 'ui-monospace, "SF Mono", "JetBrains Mono", "Menlo", "Consolas", monospace',
};

export const DIALOG_CONTENT_WIDTH = 380;

export const formatValue = (value: number | undefined | null): string | null => {
  if (value === null || value === undefined) return null;
  return parseLocaleNumber(value);
};

/** Negative with the minus sign (U+2212): "−3.000". */
export const formatNegative = (value: number): string => `−${parseLocaleNumber(Math.abs(value))}`;

/** The calculation as subtle text ("2.400 × 5"); null when usage is 1 and adds nothing. */
export const calcText = (quantity: number, usage?: number): string | null => {
  if (!usage || usage === 1) return null;
  return `${parseLocaleNumber(quantity)} × ${usage}`;
};

type Tone = "used" | "left" | "lent" | "neg" | "faint" | "ink";

const toneColor: Record<Tone, string> = {
  used: T.used,
  left: T.left,
  lent: T.lent,
  neg: T.neg,
  faint: T.inkFaint,
  ink: T.ink,
};

const tintBackground: Record<"used" | "left" | "lent", string> = {
  used: T.usedSoft,
  left: T.leftSoft,
  lent: T.lentSoft,
};

/** An extract group: own padding and bottom border, fixed Dialog width. */
export const ExtractCard: React.FC<{ children: React.ReactNode; last?: boolean }> = ({ children, last }) => (
  <Box
    sx={{
      width: DIALOG_CONTENT_WIDTH,
      padding: "16px 18px",
      borderBottom: last ? "none" : `1px solid ${T.line}`,
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    }}
  >
    {children}
  </Box>
);

export const CardHeader: React.FC<{
  railColor: string;
  name: string;
  kind?: string;
  right?: React.ReactNode;
}> = ({ railColor, name, kind, right }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
    <Box sx={{ display: "flex", alignItems: "stretch", gap: "8px", minWidth: 0 }}>
      <Box sx={{ width: "3px", borderRadius: "2px", background: railColor, flex: "none" }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: T.mono,
            fontSize: "12.5px",
            fontWeight: 600,
            color: T.ink,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </Typography>
        {kind && (
          <Typography
            sx={{
              fontSize: "10px",
              letterSpacing: ".09em",
              textTransform: "uppercase",
              fontWeight: 600,
              color: T.inkFaint,
            }}
          >
            {kind}
          </Typography>
        )}
      </Box>
    </Box>
    {right}
  </Box>
);

const chipStyle: Record<"pr" | "alt" | "sub" | "use", object> = {
  pr: { background: T.accentSoft, color: T.accentInk, border: "none" },
  sub: { background: T.subSoft, color: T.sub, border: "none" },
  alt: { background: T.surfaceSunken, color: T.inkFaint, border: `1px solid ${T.line}` },
  use: { background: "transparent", color: T.inkFaint, border: `1px solid ${T.lineStrong}` },
};

export const Chip: React.FC<{ variant: "pr" | "alt" | "sub" | "use"; children: React.ReactNode }> = ({
  variant,
  children,
}) => (
  <Typography
    component="span"
    sx={{
      fontFamily: T.mono,
      fontSize: "10px",
      letterSpacing: ".03em",
      padding: "1px 5px",
      borderRadius: "3px",
      fontWeight: 600,
      whiteSpace: "nowrap",
      ...chipStyle[variant],
    }}
  >
    {children}
  </Typography>
);

export const CellLabel: React.FC<{ children: React.ReactNode; mono?: boolean }> = ({ children, mono }) => (
  <Typography
    component="span"
    sx={{ fontFamily: mono ? T.mono : "Inter", fontSize: mono ? "12.5px" : "13px", color: T.ink }}
  >
    {children}
  </Typography>
);

export const Calc: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="span"
    sx={{ fontFamily: T.mono, fontSize: "11px", color: T.inkFaint, fontVariantNumeric: "tabular-nums" }}
  >
    {children}
  </Typography>
);

/**
 * Reference to a related code, with an arrow and the code highlighted:
 * "↳ CODE semi-finished" (forecast) or "→ CODE" (the BOM the stock was allocated to).
 */
export const ParentRef: React.FC<{ code: string; label?: string; arrow?: string }> = ({
  code,
  label,
  arrow = "↳",
}) => (
  <Typography
    component="span"
    sx={{ fontSize: "11px", color: T.inkFaint, display: "inline-flex", alignItems: "center", gap: "4px" }}
  >
    {arrow}
    <Box component="b" sx={{ fontFamily: T.mono, fontWeight: 600, color: T.inkSoft, fontSize: "11px" }}>
      {code}
    </Box>
    {label}
  </Typography>
);

export const Qty: React.FC<{ tone?: Tone; children: React.ReactNode }> = ({ tone = "ink", children }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: T.mono,
      fontVariantNumeric: "tabular-nums",
      fontSize: "13px",
      fontWeight: 600,
      whiteSpace: "nowrap",
      color: toneColor[tone],
    }}
  >
    {children}
  </Typography>
);

/** Grid row "content | quantity", with an optional tinted background. */
export const Row: React.FC<{
  tint?: "used" | "left" | "lent";
  left: React.ReactNode;
  qty: React.ReactNode;
}> = ({ tint, left, qty }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr auto",
      alignItems: "center",
      gap: "12px",
      padding: "5px 8px",
      borderRadius: "4px",
      background: tint ? tintBackground[tint] : "transparent",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0, flexWrap: "wrap" }}>{left}</Box>
    {qty}
  </Box>
);

export const Rows: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>{children}</Box>
);

/** Inner card footer: uppercase label + right-aligned total. */
export const SummaryRow: React.FC<{ label: string; value: React.ReactNode; tone?: Tone }> = ({
  label,
  value,
  tone = "ink",
}) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
    <Typography
      sx={{
        fontSize: "10px",
        letterSpacing: ".09em",
        textTransform: "uppercase",
        fontWeight: 600,
        color: T.inkFaint,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: T.mono,
        fontSize: "13px",
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
        color: toneColor[tone],
      }}
    >
      {value}
    </Typography>
  </Box>
);

/** Sunken strip at the end of the dialog: label + large value. */
export const Foot: React.FC<{ label: string; value: React.ReactNode; valueColor?: string }> = ({
  label,
  value,
  valueColor = T.ink,
}) => (
  <Box
    sx={{
      background: T.surfaceSunken,
      borderTop: `1px solid ${T.lineStrong}`,
      padding: "14px 18px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: "12px",
    }}
  >
    <Typography
      sx={{ fontSize: "12px", fontWeight: 600, color: T.inkSoft, textTransform: "uppercase", letterSpacing: ".07em" }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: T.mono,
        fontSize: "15px",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        color: valueColor,
      }}
    >
      {value}
    </Typography>
  </Box>
);

/** Dialog title bar, in the primary color, with the PN in monospace. */
export const DialogBar: React.FC<{ label: string; pn: string; onClose: () => void }> = ({ label, pn, onClose }) => (
  <Box
    sx={{
      alignSelf: "stretch",
      width: "100%",
      boxSizing: "border-box",
      background: T.accent,
      color: "#fff",
      padding: "13px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
    }}
  >
    {/* Title container — left-aligned */}
    <Box
      sx={{
        flex: "1 1 auto",
        minWidth: 0,
        overflow: "hidden",
        display: "flex",
        alignItems: "baseline",
        gap: "8px",
      }}
    >
      <Typography
        component="span"
        sx={{ fontSize: "14px", fontWeight: 700, color: "#fff", whiteSpace: "nowrap", flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontFamily: T.mono,
          fontSize: "12px",
          color: "#fff",
          opacity: 0.85,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
      >
        {pn}
      </Typography>
    </Box>
    {/* Close button — right-aligned */}
    <IconButton
      onClick={onClose}
      aria-label="close"
      size="small"
      sx={{
        flex: "0 0 auto",
        color: "#fff",
        opacity: 0.85,
        padding: "2px",
        "&:hover": { opacity: 1, background: "rgba(255,255,255,0.12)" },
      }}
    >
      <Close sx={{ fontSize: "18px" }} />
    </IconButton>
  </Box>
);
